import { useState, useEffect, FC } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import emailjs from "@emailjs/browser";
import { useLocation } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";

interface FormData {
  name: string;
  email: string;
  phone: string;
  type: string;
  message: string;
}

export const ContactForm: FC = () => {
  const location = useLocation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>();
  const [isVending, setIsVending] = useState(
    location.pathname.includes("vending")
  );
  const [status, setStatus] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    try {
      setLoading(true);
      setStatus("");

      // Initialize EmailJS with your User ID
      emailjs.init(process.env.REACT_APP_EMAILJS_USER_ID!);

      // Send email using EmailJS
      const result = await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID!,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID!,
        {
          name: data.name,
          email: data.email,
          phone: data.phone,
          type: isVending ? 'Vending/Atm' : 'AirBnb',
          message: data.message,
        }
      );

      console.log("Info sent:", result.text);
      setStatus("success");
      reset();
    } catch (error) {
      console.error("Info sending failed:", error);
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setIsVending(location.pathname.includes("vending"));
  }, [location.pathname]);

  return (
    <Box
      sx={{
        maxWidth: 500,
        mx: "auto",
        p: 3,
        bgcolor: "background.paper",
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <Typography variant="h5" component="h2" align="center" gutterBottom>
        Contact Us
      </Typography>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <TextField
          label="Name"
          fullWidth
          {...register("name", { required: "Name is required" })}
          error={!!errors.name}
          helperText={errors.name?.message}
          variant="outlined"
        />
        <TextField
          label="Email"
          type="email"
          fullWidth
          {...register("email", {
            required: "Email is required",
            pattern: {
              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
              message: "Invalid email address",
            },
          })}
          error={!!errors.email}
          helperText={errors.email?.message}
          variant="outlined"
        />
        <TextField
          label="Phone Number"
          type="tel"
          fullWidth
          {...register("phone", {
            required: "Phone number is required",
            pattern: {
              value: /^\+?[1-9]\d{1,14}$/,
              message: "Invalid phone number",
            },
          })}
          error={!!errors.phone}
          helperText={errors.phone?.message}
          variant="outlined"
        />
        <TextField
          label="Message"
          multiline
          rows={4}
          fullWidth
          {...register("message", { required: "Message is required" })}
          error={!!errors.message}
          helperText={errors.message?.message}
          variant="outlined"
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          sx={{ mt: 2 }}
        >
          {loading ? "Sending..." : "Submit"}
        </Button>
      </Box>
      {status === "success" && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Message sent successfully!
        </Alert>
      )}
      {status === "error" && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to send Message. Please try again.
        </Alert>
      )}
    </Box>
  );
};
